<template>
  <div class="viewer-xlsx">
    <div v-if="parsing" class="viewer-xlsx__loading">Carregando planilha...</div>

    <template v-else-if="sheetNames.length">
      <div v-if="sheetNames.length > 1" class="viewer-xlsx__tabs">
        <button
          v-for="name in sheetNames"
          :key="name"
          type="button"
          class="viewer-xlsx__tab"
          :class="{ 'is-active': name === activeSheet }"
          @click="selectSheet(name)"
        >
          {{ name }}
        </button>
      </div>
      <div class="viewer-xlsx__table" v-html="tableHtml"></div>
    </template>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'ViewerXlsx',
  props: {
    src: { type: [Blob, ArrayBuffer, String], default: null },
  },
  data() {
    return {
      alive: true,
      parsing: false,
      workbook: null,
      sheetNames: [],
      activeSheet: null,
      tableHtml: '',
    };
  },
  watch: {
    src: {
      immediate: true,
      handler(value) {
        this.loadWorkbook(value);
      },
    },
  },
  beforeDestroy() {
    this.alive = false;
    this.workbook = null;
  },
  methods: {
    async toArrayBuffer(value) {
      if (!value) return null;
      if (value instanceof ArrayBuffer) return value;
      if (typeof Blob !== 'undefined' && value instanceof Blob) {
        return value.arrayBuffer();
      }
      if (typeof value === 'string') {
        const res = await fetch(value);
        return res.arrayBuffer();
      }
      return null;
    },
    async loadWorkbook(value) {
      this.parsing = true;
      this.workbook = null;
      this.sheetNames = [];
      this.activeSheet = null;
      this.tableHtml = '';

      if (!value) {
        this.parsing = false;
        return;
      }

      try {
        const buffer = await this.toArrayBuffer(value);
        if (!this.alive || value !== this.src) return;
        if (!buffer || !buffer.byteLength) {
          throw new Error('Arquivo vazio ou inválido');
        }

        const bytes = new Uint8Array(buffer);
        // XLSX (ZIP) começa com PK; XLS antigo começa com D0 CF 11 E0
        const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b;
        const isOle =
          bytes[0] === 0xd0 &&
          bytes[1] === 0xcf &&
          bytes[2] === 0x11 &&
          bytes[3] === 0xe0;
        if (!isZip && !isOle) {
          throw new Error('O arquivo não parece ser uma planilha Excel válida');
        }

        const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
        if (!this.alive || value !== this.src) return;

        const names = workbook.SheetNames || [];
        if (!names.length) {
          throw new Error('Planilha sem abas');
        }

        this.workbook = workbook;
        this.sheetNames = names;
        this.selectSheet(names[0]);
        this.$emit('rendered');
      } catch (err) {
        if (!this.alive) return;
        this.workbook = null;
        this.sheetNames = [];
        this.tableHtml = '';
        this.$emit('error', err);
      } finally {
        if (this.alive) this.parsing = false;
      }
    },
    selectSheet(name) {
      if (!this.workbook || !name) return;
      const sheet = this.workbook.Sheets[name];
      if (!sheet) {
        this.$emit('error', new Error(`Aba "${name}" não encontrada`));
        return;
      }
      this.activeSheet = name;
      // sheet_to_html gera tabela; limpar atributos de estilo excessivos não é necessário
      this.tableHtml = XLSX.utils.sheet_to_html(sheet, { id: 'dv-xlsx-table', editable: false });
    },
  },
};
</script>

<style scoped lang="scss">
.viewer-xlsx {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #222;
  overflow: hidden;
}

.viewer-xlsx__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
}

.viewer-xlsx__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.4rem 0.6rem;
  background: #f0f2f5;
  border-bottom: 1px solid #dde1e7;
  flex-shrink: 0;
}

.viewer-xlsx__tab {
  appearance: none;
  border: 1px solid #ccd2db;
  background: #fff;
  color: #333;
  border-radius: 0.25rem;
  padding: 0.2rem 0.55rem;
  font-size: 0.8rem;
  cursor: pointer;

  &.is-active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: #fff;
  }
}

.viewer-xlsx__table {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0.5rem;
}

.viewer-xlsx__table ::v-deep table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 0.85rem;
}

.viewer-xlsx__table ::v-deep td,
.viewer-xlsx__table ::v-deep th {
  border: 1px solid #d0d5dd;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
  vertical-align: top;
}

.viewer-xlsx__table ::v-deep th {
  background: #f5f7fa;
  font-weight: 600;
}
</style>
